import { OneWorkoutCard } from "@/components/workouts/OneWorkoutCard";
import PageTitleScript from "@/components/MetaScript";
import { useMyWorkouts } from "@/hooks/useMyWorkouts";
import { useParams } from "react-router-dom";
import { TopNavByDates } from "@/components/workouts/TopNavByDates";
import { CustomLoader } from "@/components/ui/CustomLoader";
import { useState, useEffect, useRef } from "react";
import { useOneTimeHelper } from "@/hooks/useOneTimeHelper";
import DeleteSetHelper from "@/components/workouts/DeleteSetHelper";
import { ReceivedInvites } from "@/components/mates/ReceivedInvites";
import { useMatesWithPending } from "@/hooks/useMates";

interface ActiveDates {
  later_dates: string[];
  current: string;
  earlier_dates: string[];
}

export function WorkoutsPage() {
  // Use the useParams hook to get the route parameters
  const { date } = useParams<{ date: string }>();

  const { showHelper, dismissHelper } = useOneTimeHelper(
    "helper:delete-set",
    2000
  );

  const [currentDate, setCurrentDate] = useState(date ?? undefined);
  // TanStack useQuery 会自动更新 data
  const { data, isLoading } = useMyWorkouts(currentDate ?? undefined);
  
  // Fetch mate data to show received invites
  const { data: matesData } = useMatesWithPending();
  const receivedInvites = matesData?.received_invites || [];

  // 缓存上一次的 activeDates：当 isLoading 时继续使用缓存，而不是空的 data?.active_dates
  const previousActiveDatesRef = useRef<ActiveDates | undefined>(undefined);

  // 每次成功加载 data，就缓存 active_dates
  useEffect(() => {
    if (data?.workouts && data?.workouts.length > 0 && data?.active_dates) {
      previousActiveDatesRef.current = data.active_dates;
    }
  }, [data?.workouts, data?.active_dates]);

  // 传入 TopNavByDates 的数据
  const activeDatesForTopNav =
    data?.active_dates ?? previousActiveDatesRef.current;

  /**
   * 如果 currentDate 尚未设置，并且 data 中包含 active_dates.current，就设置为默认高亮
   */
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && !currentDate) {
      let defaultDate = null;
      if (data?.workouts && data?.workouts.length > 0) {
        defaultDate = data.active_dates.current;
      } else {
        if (
          data?.active_dates?.earlier_dates &&
          data?.active_dates?.earlier_dates.length > 0
        ) {
          defaultDate = data.active_dates.earlier_dates[0];
        }
      }

      if (defaultDate) {
        hasInitializedRef.current = true;
        setCurrentDate(defaultDate);
        window.history.replaceState({}, "", `/workouts/date/${defaultDate}`);
      }
    }
  }, [currentDate, data?.active_dates, data?.workouts]);

  if (!isLoading && (!data?.workouts || data.workouts.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-lg text-tx-muted">
          You haven't tracked any workouts yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitleScript title="Your Workouts" />

      {/* Show received workout mate invitations if any */}
      {receivedInvites.length > 0 && (
        <div className="px-6">
          <ReceivedInvites invites={receivedInvites} />
        </div>
      )}

      {/* A horizontal menu based on active_dates */}
      <TopNavByDates
        activeDates={activeDatesForTopNav}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
      />

      {/* Workouts List */}
      {showHelper && <DeleteSetHelper onClose={dismissHelper} />}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <CustomLoader />
        </div>
      ) : (
        <div className="space-y-3 px-6">
          {currentDate &&
            data?.workouts?.map((workout) => (
              <OneWorkoutCard
                key={workout.nano_id}
                workout={workout}
                sets={data.sets}
              />
            ))}
        </div>
      )}
    </div>
  );
}
